const normalizeSemanticValue = (value) => String(value || '')
  .trim()
  .toLowerCase()
  .replace(/[\s-]+/g, '_');

const INTERNAL_TRANSFER_VALUES = new Set([
  'internal_transfer',
  'linked_internal_transfer',
  'connected_account_transfer',
]);

const DEBT_PAYMENT_VALUES = new Set([
  'debt_payment',
]);

const isExplicitTrue = (value) => value === true || value === 1 || value === '1' || value === 'true';

export const getTransferPairId = (transaction = {}) => (
  transaction?.transferPairId
  ?? transaction?.transfer_pair_id
  ?? transaction?.internalTransferPairId
  ?? transaction?.internal_transfer_pair_id
  ?? null
);

export const getFinancialSemantic = (transaction = {}) => normalizeSemanticValue(
  transaction?.financialSemantic
  ?? transaction?.financialSemantics
  ?? transaction?.financial_semantic
  ?? transaction?.financial_semantics
  ?? transaction?.transactionSemantic
  ?? transaction?.transaction_semantic
  ?? transaction?.semanticType
  ?? transaction?.semantic_type
);

export const isInternalTransfer = (transaction = {}) => {
  // A linked debt payment keeps the pair for reconciliation, but only its
  // liability-side credit is an excluded internal-transfer leg.
  if (DEBT_PAYMENT_VALUES.has(getFinancialSemantic(transaction))) return false;
  if (isExplicitTrue(transaction?.isInternalTransfer ?? transaction?.is_internal_transfer)) return true;
  if (getTransferPairId(transaction) != null) return true;
  return INTERNAL_TRANSFER_VALUES.has(getFinancialSemantic(transaction));
};

export const isDebtPayment = (transaction = {}) => (
  DEBT_PAYMENT_VALUES.has(getFinancialSemantic(transaction))
);

export const isExcludedFromFinancialTotals = (transaction = {}) => {
  if (isInternalTransfer(transaction)) return true;
  if (isExplicitTrue(
    transaction?.excludedFromFinancialTotals
    ?? transaction?.excluded_from_financial_totals
    ?? transaction?.excludeFromFinancialTotals
    ?? transaction?.exclude_from_financial_totals
  )) return true;

  const parentName = normalizeSemanticValue(
    transaction?.parentCategoryName ?? transaction?.parent_category_name
  );
  const categoryName = normalizeSemanticValue(
    transaction?.categoryName ?? transaction?.category_name
  );
  const categoryKey = normalizeSemanticValue(
    transaction?.categoryKey ?? transaction?.category_key
  );
  return categoryKey === 'excluded:transfer'
    || categoryKey === 'excluded:transfers'
    || (parentName === 'excluded' && (categoryName === 'transfer' || categoryName === 'transfers'));
};

export const normalizeFinancialSemantics = (transaction = {}) => {
  const record = transaction || {};
  const transferPairId = getTransferPairId(record);
  const financialSemantic = getFinancialSemantic(record) || null;
  const debtPayment = isDebtPayment(record);
  const internalTransfer = isInternalTransfer(record);
  const excludedFromFinancialTotals = isExcludedFromFinancialTotals(record);

  return {
    financialSemantic: internalTransfer ? 'internal_transfer' : financialSemantic,
    isInternalTransfer: internalTransfer,
    isDebtPayment: debtPayment,
    transferPairId,
    transferRole: record.transferRole
      ?? record.transfer_role
      ?? record.internalTransferRole
      ?? record.internal_transfer_role
      ?? null,
    transferMatchSource: record.transferMatchSource
      ?? record.transfer_match_source
      ?? record.financialSemanticsSource
      ?? record.financial_semantics_source
      ?? null,
    excludedFromFinancialTotals,
    excludedFromIncome: excludedFromFinancialTotals || debtPayment || isExplicitTrue(
      record.excludedFromIncome ?? record.excluded_from_income
    ),
    excludedFromSpending: excludedFromFinancialTotals || isExplicitTrue(
      record.excludedFromSpending ?? record.excluded_from_spending
    ),
    excludedFromBudgets: excludedFromFinancialTotals || isExplicitTrue(
      record.excludedFromBudgets ?? record.excluded_from_budgets
    ),
    excludedFromReports: excludedFromFinancialTotals || isExplicitTrue(
      record.excludedFromReports ?? record.excluded_from_reports
    ),
    excludedFromReview: excludedFromFinancialTotals || debtPayment || isExplicitTrue(
      record.excludedFromReview ?? record.excluded_from_review
    ),
  };
};
